import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { HttpPortProtocol } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import type { GPUSpec } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import type { ExternalNode } from "components/Environment/RegisteredCompute/useNodes";
import { useNodes } from "components/Environment/RegisteredCompute/useNodes";
import { ExternalNodeService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/external_node_connect";
import { createConnectClient } from "server/connect";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";
import { productNameToDisplayName } from "components/NodeDetail/BrevConnect/shared";
import { ShareSecureLinkDialog } from "components/NodeDetail/BrevConnect/NodeSecureLinksSection";
import { RemoveNodeDialog } from "components/Environment/RegisteredCompute/RemoveNodeDialog";
import { formatGpuName } from "utils/gpuNames";
import { toast } from "utils/toast";
import { getSSHPortId } from "components/NodeDetail/Environment/TabContent/Access/SSHAccess/addSSHAccessToEnvironmentRequest";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "components/Graphs/Dropdown";
import { EditNetworkModeDialog } from "./EditNetworkModeDialog";
import { resolveNetworkMemberType } from "components/Environment/Networks/networkMode";
import { useQueryClient } from "@tanstack/react-query";
import { organizationKeys } from "server/devplane/organizations/queries";
import {
  ComputeCardTemplate,
  ComputeCardMenu,
  SSH_NOT_ENABLED_REASON,
  type ComputeCardMetadata,
} from "./ComputeCardTemplate";
import {
  ComputeCardKindLabel,
  ComputeCardOwner,
  ComputeCardRelativeTime,
  ComputeNetworkMode,
} from "./ComputeCardMetadata";
import {
  SSH_ACCESS_HASH,
  SSH_ACCESS_NAVIGATION_STATE,
} from "components/Environment/shared/sshAccessNavigation";
import {
  DGX_RACK_BACKGROUND_FILTER,
  DGX_RACK_TEXTURE_BACKGROUND_FILTER,
  computeRackGoldTrianglePatternStyle,
} from "components/Environment/shared/ComputeDetail/rackSurface";
import {
  EnvironmentApplicationChip,
  type EnvironmentApplicationAccess,
  type EnvironmentApplicationAccessAudience,
} from "./EnvironmentApplicationChip";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { canShareSecureLinkForCompute } from "./secureLinkSharingPermissions";
import {
  getCurrentUserAccessIdentities,
  getSecureLinkAccessAudience,
  getSecureLinkAccessLevel,
} from "components/Environment/utils/secureLinkAccess";
import {
  ComputeHardwareInlineSummary,
  ComputeHardwareSummary,
} from "./ComputeHardwareSummary";
import { formatGpuVramFromBytes } from "./computeHardwareFormat";

const networkNodeClient = createConnectClient(ExternalNodeService);

export const hasLocalComputeSshApplication = (
  node: Pick<ExternalNode, "ports">,
) => Boolean(getSSHPortId(node.ports));

export const getLocalComputeSshDisabledReason = (
  node: Pick<ExternalNode, "ports">,
) => (hasLocalComputeSshApplication(node) ? undefined : SSH_NOT_ENABLED_REASON);

type LocalPortActionCandidate = {
  access: EnvironmentApplicationAccess;
  accessAudience?: EnvironmentApplicationAccessAudience;
  hostname?: string;
};

export const getPrimaryLocalComputePortAction = <
  T extends LocalPortActionCandidate,
>(
  candidates: readonly T[],
): T | undefined =>
  candidates.find(
    ({ access, hostname }) => access !== "inaccessible" && Boolean(hostname),
  ) ?? candidates.find(({ hostname }) => Boolean(hostname));

const getLocalPortAccessAudience = (
  port: DisplayPort,
): EnvironmentApplicationAccessAudience | undefined => {
  const audience = getSecureLinkAccessAudience(port);
  return audience === "public" ? undefined : audience;
};

type DgxProductName = "DGX Spark" | "DGX Station";
const DGX_PRODUCT_HARDWARE_GRADIENT_CLASS_NAME =
  "bg-[linear-gradient(90deg,#f3f0ec_0%,#eadab8_25%,#ddc191_50%,#eadab8_75%,#f3f0ec_100%)]";
const DGX_RACK_MAIN_SURFACE_COLOR = "#2f2e2ea1";
const DGX_SPARK_RACK_SURFACE_COLOR = DGX_RACK_MAIN_SURFACE_COLOR;

const isDgxProductName = (
  productName: string | undefined,
): productName is DgxProductName =>
  productName === "DGX Spark" || productName === "DGX Station";

const getDgxRackSurfaceStyle = (
  productName: DgxProductName,
): React.CSSProperties => ({
  backgroundColor:
    productName === "DGX Spark"
      ? DGX_SPARK_RACK_SURFACE_COLOR
      : DGX_RACK_MAIN_SURFACE_COLOR,
  boxShadow: `
    0 18px 36px rgba(0, 0, 0, 0.45),
    inset 0 0 18px rgba(28, 27, 25, 0.42)
  `,
});

export const dgxRackSurfaceStyle = getDgxRackSurfaceStyle("DGX Station");
export const dgxRackCompactSurfaceStyle = getDgxRackSurfaceStyle("DGX Station");
export const dgxRackStackedSurfaceStyle: React.CSSProperties = {
  backgroundColor: DGX_RACK_MAIN_SURFACE_COLOR,
  boxShadow: dgxRackSurfaceStyle.boxShadow,
};

export const getLocalComputeCardPatternStyles = (
  productName: string | undefined,
) =>
  isDgxProductName(productName)
    ? {
        surfaceStyle: getDgxRackSurfaceStyle(productName),
        compactSurfaceStyle: getDgxRackSurfaceStyle(productName),
        stackedSurfaceStyle: {
          backgroundColor:
            productName === "DGX Spark"
              ? DGX_SPARK_RACK_SURFACE_COLOR
              : DGX_RACK_MAIN_SURFACE_COLOR,
          boxShadow: dgxRackSurfaceStyle.boxShadow,
        },
        surfaceBackgroundFilter: DGX_RACK_BACKGROUND_FILTER,
        statusActionClusterStyle: computeRackGoldTrianglePatternStyle,
        statusActionClusterBackgroundFilter: DGX_RACK_TEXTURE_BACKGROUND_FILTER,
        borderWidthClassName: "border-0",
        borderRadiusClassName: "rounded-[3px]",
        metadataSurfaceBackgroundClassName: "bg-transparent",
        dividerClassName: "bg-[#3b3a3a]",
      }
    : undefined;

const getGpuDetails = (
  gpu: Pick<GPUSpec, "count" | "memoryBytes" | "model">,
) => {
  const count = gpu.count || 1;
  const vram = formatGpuVramFromBytes(gpu.memoryBytes) || "Unknown";

  return {
    count,
    model: formatGpuName(gpu.model) || "Unknown",
    vram,
  };
};

const DgxProductHardwareLabel: React.FC<{
  productName: DgxProductName;
}> = ({ productName }) => {
  return (
    <span className="truncate text-lg font-black leading-[1.2]">
      {productName}
    </span>
  );
};

export const LocalComputeHardware: React.FC<{
  gpu?: Pick<GPUSpec, "count" | "memoryBytes" | "model">;
  productName?: string;
}> = ({ gpu, productName }) => {
  if (!gpu) {
    return (
      <ComputeHardwareSummary align="center" name="CPU only" tone="muted" />
    );
  }

  const dgxProductName = isDgxProductName(productName) ? productName : null;

  if (dgxProductName) {
    return (
      <ComputeHardwareSummary
        align="center"
        gradientClassName={DGX_PRODUCT_HARDWARE_GRADIENT_CLASS_NAME}
        name={<DgxProductHardwareLabel productName={dgxProductName} />}
      />
    );
  }

  const { count, model, vram } = getGpuDetails(gpu);
  const visibleGpuMemory = gpu.memoryBytes ? `${vram} VRAM` : "GPU";

  return (
    <ComputeHardwareSummary
      align="center"
      count={count}
      detail={<span className="truncate">{visibleGpuMemory}</span>}
      name={model}
    />
  );
};

export const getLocalComputeResponsiveHardwareLabel = ({
  gpu,
  productName,
}: {
  gpu?: Pick<GPUSpec, "count" | "memoryBytes" | "model">;
  productName?: string;
}) => {
  const dgxProductName = isDgxProductName(productName) ? productName : null;
  if (dgxProductName) return dgxProductName;
  if (!gpu) return "CPU only";

  const { count, model, vram } = getGpuDetails(gpu);
  const visibleGpuMemory = gpu.memoryBytes ? `${vram} VRAM` : "GPU";
  return `${count} x ${model} ${visibleGpuMemory}`;
};

export const LocalComputeCard: React.FC<{
  node: ExternalNode;
  isConnectivityLoading?: boolean;
}> = ({ node, isConnectivityLoading = false }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { activeOrgId, activeOrgRole } = useContext(OrgContext);
  const { me } = useContext(UserContext);
  const { removeNode, setHTTPPortAccess, updateNodeNetworkMemberType } =
    useNodes(false);
  const nodeName = node.name || node.externalNodeId;
  const starredComputeKey = node.externalNodeId
    ? `local:${node.externalNodeId}`
    : undefined;
  const gpu = node.nodeSpec?.gpus?.[0];
  const productName = productNameToDisplayName(node.nodeSpec?.productName);
  const patternStyles = getLocalComputeCardPatternStyles(productName);
  const ports = node.ports
    .filter(
      (port) =>
        (port.httpProtocol ?? HttpPortProtocol.UNSPECIFIED) !==
        HttpPortProtocol.UNSPECIFIED,
    )
    .slice(0, 3);
  const sshControlsDisabledReason = getLocalComputeSshDisabledReason(node);
  const [copiedControl, setCopiedControl] = useState<"ssh" | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [editNetworkModeOpen, setEditNetworkModeOpen] = useState(false);
  const [sharingPort, setSharingPort] = useState<DisplayPort | null>(null);
  const createdAt = node.createTime?.toDate();
  const canShareSecureLinks = canShareSecureLinkForCompute({
    activeOrgRole,
    currentUserId: me?.id,
    creatorUserId: node.createdBy,
  });
  const open = () =>
    history.push(`/org/${activeOrgId}/nodes/${node.externalNodeId}`);
  const openSshAccess = () =>
    history.push({
      pathname: `/org/${activeOrgId}/nodes/${node.externalNodeId}`,
      hash: SSH_ACCESS_HASH,
      state: SSH_ACCESS_NAVIGATION_STATE,
    });
  const copySshCommand = async () => {
    await navigator.clipboard.writeText(`brev shell ${nodeName}`);
    setCopiedControl("ssh");
    toast.success("CLI command to SSH copied to the clipboard.");
    window.setTimeout(() => setCopiedControl(null), 2000);
  };
  const shareSecureLink = async (
    authorizedEmails: string[],
    allowPublic: boolean,
  ) => {
    if (!sharingPort) return;
    await setHTTPPortAccess(sharingPort.portId, authorizedEmails, allowPublic);
  };
  const activeUserIdentities = getCurrentUserAccessIdentities(me);
  const metadata: ComputeCardMetadata[] = [
    {
      unstyled: true,
      value: <ComputeCardKindLabel kind="local" />,
    },
    {
      unstyled: true,
      value: (
        <ComputeNetworkMode
          memberType={resolveNetworkMemberType(node.networkMemberType)}
          loading={isConnectivityLoading}
        />
      ),
    },
    {
      label: "User",
      standalonePill: true,
      value: <ComputeCardOwner userId={node.createdBy} />,
    },
    ...(createdAt
      ? [
          {
            label: "Created",
            standalonePill: true,
            value: <ComputeCardRelativeTime date={createdAt} />,
          },
        ]
      : []),
  ];
  const portActionItems = ports.map((port) => {
    const access = getSecureLinkAccessLevel(port, activeUserIdentities);
    const hostname = port.hostname || undefined;
    return {
      access,
      accessAudience: getLocalPortAccessAudience(port),
      application: {
        name: port.hostname || `Port ${port.portNumber}`,
        port: port.serverPort || port.portNumber,
        labels: {
          "cta-value": port.hostname || `Port ${port.portNumber}`,
        },
      },
      hostname,
      port,
    };
  });
  const primaryPortAction = getPrimaryLocalComputePortAction(portActionItems);

  return (
    <>
      <ComputeCardTemplate
        compactAtMedium
        borderWidthClassName={patternStyles?.borderWidthClassName}
        borderRadiusClassName={patternStyles?.borderRadiusClassName}
        compactSurfaceStyle={patternStyles?.compactSurfaceStyle}
        stackedSurfaceStyle={patternStyles?.stackedSurfaceStyle}
        surfaceStyle={patternStyles?.surfaceStyle}
        surfaceBackgroundFilter={patternStyles?.surfaceBackgroundFilter}
        statusActionClusterStyle={patternStyles?.statusActionClusterStyle}
        statusActionClusterBackgroundFilter={
          patternStyles?.statusActionClusterBackgroundFilter
        }
        metadataSurfaceBackgroundClassName={
          patternStyles?.metadataSurfaceBackgroundClassName
        }
        dividerClassName={patternStyles?.dividerClassName}
        onOpen={open}
        title={<span>{nodeName}</span>}
        starredComputeKey={starredComputeKey}
        metadata={metadata}
        hardware={<LocalComputeHardware gpu={gpu} productName={productName} />}
        responsiveHardware={
          <ComputeHardwareInlineSummary
            gradientClassName={
              isDgxProductName(productName)
                ? DGX_PRODUCT_HARDWARE_GRADIENT_CLASS_NAME
                : undefined
            }
            tone={gpu ? "highlight" : "muted"}
          >
            {getLocalComputeResponsiveHardwareLabel({
              gpu,
              productName,
            })}
          </ComputeHardwareInlineSummary>
        }
        onCopySsh={() => void copySshCommand()}
        sshControlsDisabledReason={sshControlsDisabledReason}
        copiedControl={copiedControl}
        copySuccessClassName="text-highlight"
        primaryAction={
          primaryPortAction ? (
            <EnvironmentApplicationChip
              application={primaryPortAction.application}
              hostname={primaryPortAction.hostname}
              environmentName={nodeName}
              access={primaryPortAction.access}
              accessAudience={primaryPortAction.accessAudience}
              onShare={
                canShareSecureLinks && primaryPortAction.hostname
                  ? () => setSharingPort(primaryPortAction.port)
                  : undefined
              }
            />
          ) : undefined
        }
        menu={
          <ComputeCardMenu label="Node actions">
            <DropdownMenuLabel>Access</DropdownMenuLabel>
            <DropdownMenuItem onSelect={openSshAccess}>
              Edit SSH access
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Network</DropdownMenuLabel>
            <DropdownMenuItem
              disabled={!isOrgAdminOrMaintainerRole(activeOrgRole)}
              onSelect={() => setEditNetworkModeOpen(true)}
            >
              Edit Network Mode
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-red-400"
              onSelect={() => setRemoveDialogOpen(true)}
            >
              Remove
            </DropdownMenuItem>
          </ComputeCardMenu>
        }
      />
      <EditNetworkModeDialog
        name={nodeName}
        memberType={resolveNetworkMemberType(node.networkMemberType)}
        open={editNetworkModeOpen}
        onOpenChange={setEditNetworkModeOpen}
        onSetMode={async (networkMemberType) => {
          await networkNodeClient.setNetworkMemberType({
            externalNodeId: node.externalNodeId,
            networkMemberType,
          });
          updateNodeNetworkMemberType(node.externalNodeId, networkMemberType);
          void queryClient.invalidateQueries({
            queryKey: [
              ...organizationKeys.detail(activeOrgId),
              "compute-resources",
            ],
          });
        }}
      />
      <RemoveNodeDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        nodeName={nodeName}
        onConfirm={() => removeNode(node.externalNodeId)}
      />
      {sharingPort ? (
        <ShareSecureLinkDialog
          open={sharingPort !== null}
          onOpenChange={(open) => {
            if (!open) setSharingPort(null);
          }}
          port={sharingPort}
          onSubmit={shareSecureLink}
        />
      ) : null}
    </>
  );
};
